import { useMutation } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import type { TApiODIN } from "@/apis/odin/apiODIN";
import type { TSliceNotifications } from "@/store/notifications/sliceNotifications";
import { userKeys, databaseKeys } from "@/queries/queryKeys";

export const createSettingsMutationHooks = ({
    apiODIN,
    queryClient,
    addNotificationWithDefaultTimeout,
    addNotification,
    removeNotification,
}: {
    apiODIN: TApiODIN;
    queryClient: QueryClient;
    addNotificationWithDefaultTimeout: TSliceNotifications["addNotificationWithDefaultTimeout"];
    addNotification: TSliceNotifications["addNotification"];
    removeNotification: TSliceNotifications["removeNotification"];
}) => {
    const useSyncDatabasesMutation = () =>
        useMutation({
            mutationFn: async () => {
                const toastId = addNotification({
                    kind: "toast",
                    title: "Syncing databases...",
                    severity: "info",
                });
                try {
                    await apiODIN.syncDatabases();
                    removeNotification(toastId);
                } catch (error) {
                    removeNotification(toastId);
                    throw error;
                }
            },
            onSuccess: () => {
                void queryClient.invalidateQueries({ queryKey: userKeys.all });
                void queryClient.invalidateQueries({
                    queryKey: databaseKeys.all,
                });
                addNotificationWithDefaultTimeout({
                    kind: "toast",
                    title: "Databases synced successfully",
                    severity: "success",
                });
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    kind: "toast",
                    title: "Error syncing databases",
                    severity: "danger",
                });
            },
        });

    return {
        useSyncDatabasesMutation,
    };
};

export type TSettingsMutationHooks = ReturnType<
    typeof createSettingsMutationHooks
>;
